/**
 * The statement-layout editor.
 *
 * Saving here CREATES A VERSION. Reusing an existing code makes version N+1 of
 * that layout and leaves version N exactly as it was, which is why the dialog
 * says so rather than presenting itself as an edit. Seeding from an existing
 * layout copies its lines so the common case — "the same, with one more line" —
 * is not retyped.
 *
 * The line kinds offered depend on the statement: only a balance sheet has
 * undistributed profit, and only the cash-flow statement has a cash population
 * and a reconciling line. Only `detail` and `cash` lines may carry account
 * rules; every other kind is computed, and letting one map accounts too would
 * count the same movement twice. The form drops a line's rules when its kind
 * stops being able to hold them rather than sending rules the server will
 * refuse.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@erp/ui";

import { newIdempotencyKey } from "@/features/shared/ops-api";

import {
  CASH_FLOW_ACTIVITIES,
  RULE_KINDS,
  STATEMENT_TYPES,
  createStatementLayout,
  kindMapsAccounts,
  kindsFor,
  type CashFlowActivity,
  type CreateStatementLayoutRequest,
  type StatementLayout,
  type StatementLayoutRule,
  type StatementLineKind,
  type StatementRuleKind,
  type StatementType,
} from "../statement-layouts-api";

const ACCOUNT_TYPES = ["asset", "liability", "equity", "income", "expense"] as const;

interface LineDraft {
  code: string;
  label: string;
  kind: StatementLineKind;
  ordinal: string;
  parent_code: string;
  sign_flip: boolean;
  cash_flow_activity: CashFlowActivity | "";
  rules: StatementLayoutRule[];
}

const blankLine = (ordinal: number): LineDraft => ({
  code: "",
  label: "",
  kind: "detail",
  ordinal: String(ordinal),
  parent_code: "",
  sign_flip: false,
  cash_flow_activity: "",
  rules: [],
});

function seedLines(seed: StatementLayout | null): LineDraft[] {
  if (seed === null) return [blankLine(10)];
  return seed.lines
    .map((line) => ({
      code: line.code,
      label: line.label,
      kind: line.kind,
      ordinal: String(line.ordinal),
      parent_code: line.parent_code ?? "",
      sign_flip: line.sign_flip,
      cash_flow_activity: line.cash_flow_activity ?? ("" as const),
      rules: line.rules.map((rule) => ({ ...rule })),
    }))
    .sort((left, right) => Number(left.ordinal) - Number(right.ordinal));
}

export interface StatementLayoutEditorProps {
  /** null authors a new layout; a layout seeds the next version of its code. */
  seed: StatementLayout | null;
  onClose: () => void;
  onSaved: (layout: StatementLayout) => void;
}

export function StatementLayoutEditor({ seed, onClose, onSaved }: StatementLayoutEditorProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(seed?.code ?? "");
  const [statementType, setStatementType] = useState<StatementType>(
    seed?.statement_type ?? "profit_and_loss",
  );
  const [name, setName] = useState(seed === null ? "" : `${seed.name}`);
  const [validFrom, setValidFrom] = useState(seed?.valid_from ?? "");
  const [notes, setNotes] = useState(seed?.notes ?? "");
  const [lines, setLines] = useState<LineDraft[]>(() => seedLines(seed));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowedKinds = useMemo(() => kindsFor(statementType), [statementType]);
  const parentOptions = useMemo(
    () => lines.filter((line) => line.kind === "subtotal" && line.code !== ""),
    [lines],
  );

  const setLine = (index: number, patch: Partial<LineDraft>) =>
    setLines((current) => current.map((line, at) => (at === index ? { ...line, ...patch } : line)));

  const changeKind = (index: number, kind: StatementLineKind) =>
    setLine(index, {
      kind,
      // A kind that cannot map accounts must not carry rules the server would
      // refuse, so they are dropped when the kind changes rather than at save.
      ...(kindMapsAccounts(kind) ? {} : { rules: [] }),
    });

  const complete =
    /^[a-z][a-z0-9_-]{0,62}$/.test(code) &&
    name.trim() !== "" &&
    validFrom !== "" &&
    lines.length >= 1 &&
    lines.every(
      (line) =>
        /^[a-z][a-z0-9_]{0,62}$/.test(line.code) &&
        line.label.trim() !== "" &&
        Number(line.ordinal) >= 1,
    ) &&
    new Set(lines.map((line) => line.code)).size === lines.length;

  const save = () => {
    if (!complete) return;
    setBusy(true);
    setError(null);
    const body: CreateStatementLayoutRequest = {
      code,
      statement_type: statementType,
      name: name.trim(),
      valid_from: validFrom,
      ...(notes.trim() === "" ? {} : { notes: notes.trim() }),
      lines: lines.map((line) => ({
        code: line.code,
        label: line.label.trim(),
        kind: line.kind,
        ordinal: Number(line.ordinal),
        sign_flip: line.sign_flip,
        ...(line.parent_code === "" ? {} : { parent_code: line.parent_code }),
        ...(line.cash_flow_activity === "" ? {} : { cash_flow_activity: line.cash_flow_activity }),
        // A rule carrying a field its kind does not use means something else to
        // whoever reads the row next, so only the used fields are sent.
        rules: kindMapsAccounts(line.kind)
          ? line.rules.map((rule) => ({
              kind: rule.kind,
              ...(rule.kind === "account_code_range"
                ? { code_from: rule.code_from ?? "", code_to: rule.code_to ?? "" }
                : {}),
              ...(rule.kind === "account_type" && rule.account_type !== undefined
                ? { account_type: rule.account_type }
                : {}),
              ...(rule.kind === "account_tag" && rule.account_tag !== undefined
                ? { account_tag: rule.account_tag }
                : {}),
            }))
          : [],
      })),
    };
    void createStatementLayout(body, newIdempotencyKey())
      .then((layout) => {
        onSaved(layout);
        onClose();
      })
      .catch(() =>
        setError(
          t(
            "finance.statementLayouts.createError",
            "The layout could not be saved. Check that every line has a unique code.",
          ),
        ),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {seed === null
              ? t("finance.statementLayouts.new", "New layout")
              : t("finance.statementLayouts.newVersion", "New version from this")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "finance.statementLayouts.editorHint",
              "Saving creates a version. Reusing a code makes the next version of that layout and leaves the previous one exactly as it was.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="layout-code">{t("finance.statementLayouts.code", "Code")}</Label>
            <Input
              id="layout-code"
              dir="ltr"
              className="font-mono"
              value={code}
              onChange={(event) =>
                setCode(event.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="layout-type">{t("finance.statementLayouts.type", "Statement")}</Label>
            <Select
              value={statementType}
              onValueChange={(value) => {
                const next = value as StatementType;
                setStatementType(next);
                // Kinds are statement-specific; a line holding a kind the new
                // statement does not have falls back to detail.
                setLines((current) =>
                  current.map((line) =>
                    kindsFor(next).includes(line.kind) ? line : { ...line, kind: "detail" },
                  ),
                );
              }}
              disabled={seed !== null}
            >
              <SelectTrigger id="layout-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATEMENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`finance.statementType.${type}`, type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="layout-editor-name">{t("finance.statementLayouts.name", "Name")}</Label>
            <Input
              id="layout-editor-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="layout-editor-from">
              {t("finance.statementLayouts.validFrom", "In force from")}
            </Label>
            <Input
              id="layout-editor-from"
              type="date"
              dir="ltr"
              value={validFrom}
              onChange={(event) => setValidFrom(event.target.value)}
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="layout-editor-notes">
              {t("finance.statementLayouts.notes", "Notes")}
            </Label>
            <Textarea
              id="layout-editor-notes"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-medium">
            {t("finance.statementLayouts.linesTitle", "Lines")}
          </h3>
          {lines.map((line, index) => (
            <div key={index} className="space-y-2 rounded-md border p-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_2fr_1fr_auto]">
                <Input
                  dir="ltr"
                  className="font-mono"
                  aria-label={t("finance.statementLayouts.lineCode", "Line code")}
                  placeholder={t("finance.statementLayouts.lineCode", "Line code")}
                  value={line.code}
                  onChange={(event) =>
                    setLine(index, {
                      code: event.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""),
                    })
                  }
                />
                <Input
                  aria-label={t("finance.statementLayouts.label", "Label")}
                  placeholder={t("finance.statementLayouts.label", "Label")}
                  value={line.label}
                  onChange={(event) => setLine(index, { label: event.target.value })}
                />
                <Input
                  type="number"
                  min={1}
                  max={500}
                  dir="ltr"
                  aria-label={t("finance.statementLayouts.ordinal", "Order")}
                  value={line.ordinal}
                  onChange={(event) => setLine(index, { ordinal: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={lines.length <= 1}
                  aria-label={t("finance.statementLayouts.removeLine", "Remove line")}
                  onClick={() => setLines(lines.filter((_, at) => at !== index))}
                >
                  <Trash2 className="size-4" aria-hidden="true" />
                </Button>
              </div>

              <div className="grid gap-2 sm:grid-cols-3">
                <Select
                  value={line.kind}
                  onValueChange={(value) => changeKind(index, value as StatementLineKind)}
                >
                  <SelectTrigger aria-label={t("finance.statementLayouts.kind", "Value from")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedKinds.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {t(`finance.statementLineKind.${kind}`, kind)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={line.parent_code === "" ? "__root__" : line.parent_code}
                  onValueChange={(value) =>
                    setLine(index, { parent_code: value === "__root__" ? "" : value })
                  }
                >
                  <SelectTrigger aria-label={t("finance.statementLayouts.parent", "Rolls into")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__root__">
                      {t("finance.statementLayouts.noParent", "Nothing (top level)")}
                    </SelectItem>
                    {parentOptions
                      .filter((option) => option.code !== line.code)
                      .map((option) => (
                        <SelectItem key={option.code} value={option.code}>
                          {option.label === "" ? option.code : option.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {statementType === "cash_flow" ? (
                  <Select
                    value={line.cash_flow_activity === "" ? "__none__" : line.cash_flow_activity}
                    onValueChange={(value) =>
                      setLine(index, {
                        cash_flow_activity: value === "__none__" ? "" : (value as CashFlowActivity),
                      })
                    }
                  >
                    <SelectTrigger aria-label={t("finance.statementLayouts.activity", "Activity")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t("finance.statementLayouts.noActivity", "No activity")}
                      </SelectItem>
                      {CASH_FLOW_ACTIVITIES.map((activity) => (
                        <SelectItem key={activity} value={activity}>
                          {t(`finance.cashFlowActivity.${activity}`, activity)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={line.sign_flip}
                      onCheckedChange={(checked) => setLine(index, { sign_flip: checked === true })}
                    />
                    {t("finance.statementLayouts.signFlipLabel", "Show as positive")}
                  </label>
                )}
              </div>

              {kindMapsAccounts(line.kind) ? (
                <div className="space-y-2">
                  {line.rules.map((rule, ruleIndex) => (
                    <div key={ruleIndex} className="grid gap-2 sm:grid-cols-[1fr_2fr_auto]">
                      <Select
                        value={rule.kind}
                        onValueChange={(value) =>
                          setLine(index, {
                            rules: line.rules.map((candidate, at) =>
                              at === ruleIndex
                                ? // Changing the kind clears the fields the old
                                  // kind used: a range carrying a stray account
                                  // type reads as something else entirely.
                                  { kind: value as StatementRuleKind }
                                : candidate,
                            ),
                          })
                        }
                      >
                        <SelectTrigger aria-label={t("finance.statementLayouts.ruleKind", "Rule")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RULE_KINDS.map((kind) => (
                            <SelectItem key={kind} value={kind}>
                              {t(`finance.statementRuleKind.${kind}`, kind)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {rule.kind === "account_code_range" ? (
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            dir="ltr"
                            className="font-mono"
                            aria-label={t("finance.statementLayouts.codeFrom", "From code")}
                            value={rule.code_from ?? ""}
                            onChange={(event) =>
                              setLine(index, {
                                rules: line.rules.map((candidate, at) =>
                                  at === ruleIndex
                                    ? { ...candidate, code_from: event.target.value }
                                    : candidate,
                                ),
                              })
                            }
                          />
                          <Input
                            dir="ltr"
                            className="font-mono"
                            aria-label={t("finance.statementLayouts.codeTo", "To code")}
                            value={rule.code_to ?? ""}
                            onChange={(event) =>
                              setLine(index, {
                                rules: line.rules.map((candidate, at) =>
                                  at === ruleIndex
                                    ? { ...candidate, code_to: event.target.value }
                                    : candidate,
                                ),
                              })
                            }
                          />
                        </div>
                      ) : rule.kind === "account_type" ? (
                        <Select
                          value={rule.account_type ?? "asset"}
                          onValueChange={(value) =>
                            setLine(index, {
                              rules: line.rules.map((candidate, at) =>
                                at === ruleIndex
                                  ? {
                                      ...candidate,
                                      account_type: value as StatementLayoutRule["account_type"],
                                    }
                                  : candidate,
                              ),
                            })
                          }
                        >
                          <SelectTrigger
                            aria-label={t("finance.statementLayouts.accountType", "Account type")}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACCOUNT_TYPES.map((type) => (
                              <SelectItem key={type} value={type}>
                                {t(`ledger.accounts.types.${type}`, type)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          dir="ltr"
                          className="font-mono"
                          aria-label={t("finance.statementLayouts.accountTag", "Account tag")}
                          value={rule.account_tag ?? ""}
                          onChange={(event) =>
                            setLine(index, {
                              rules: line.rules.map((candidate, at) =>
                                at === ruleIndex
                                  ? {
                                      ...candidate,
                                      account_tag: event.target.value
                                        .toLowerCase()
                                        .replace(/[^a-z0-9_]/g, ""),
                                    }
                                  : candidate,
                              ),
                            })
                          }
                        />
                      )}

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("finance.statementLayouts.removeRule", "Remove rule")}
                        onClick={() =>
                          setLine(index, {
                            rules: line.rules.filter((_, at) => at !== ruleIndex),
                          })
                        }
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLine(index, {
                        rules: [...line.rules, { kind: "account_code_range" as StatementRuleKind }],
                      })
                    }
                  >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("finance.statementLayouts.addRule", "Add a selection rule")}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t(
                    "finance.statementLayouts.computedLine",
                    "This line is computed, so it maps no accounts — that is what keeps a movement from being counted twice.",
                  )}
                </p>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLines([...lines, blankLine((lines.length + 1) * 10)])}
          >
            <Plus className="size-4" aria-hidden="true" />
            {t("finance.statementLayouts.addLine", "Add line")}
          </Button>
        </div>

        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("actions.cancel", "Cancel")}
          </Button>
          <Button type="button" disabled={!complete || busy} onClick={save}>
            {t("finance.statementLayouts.saveVersion", "Save as a new version")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
